const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://niftee-orchestrator.jason-a6e.workers.dev';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    /**
     * The parsed response body, when there was one.
     *
     * Some non-2xx responses carry data the caller needs rather than just a
     * message — a 402 from the service-charge authorize route returns a Stripe
     * client secret for completing 3D Secure. Extracting only `.error` would
     * throw that away.
     */
    public body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getAuthHeader(): string | null {
  const session = localStorage.getItem('niftee_portal_session');
  if (session) {
    try {
      const { token } = JSON.parse(session);
      if (token) {
        return `Bearer ${token}`;
      }
    } catch {
      // Ignore parse errors
    }
  }
  return null;
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const auth = getAuthHeader();

  const headers: Record<string, string> = {};

  // Don't set Content-Type for FormData — the browser sets it with the boundary
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (auth) {
    headers['Authorization'] = auth;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = 'API request failed';
    let errorBody: unknown;
    try {
      const errorData = await response.json();
      errorBody = errorData;
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // Use default error message
    }
    throw new ApiError(errorMessage, response.status, errorBody);
  }

  return response.json();
}

export function apiGet<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' });
}

export function apiPost<T>(endpoint: string, body?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

// Make a request with a specific token (used for impersonation before session is saved)
export async function apiGetWithToken<T>(
  endpoint: string,
  token: string
): Promise<T> {
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorMessage = 'API request failed';
    let errorBody: unknown;
    try {
      const errorData = await response.json();
      errorBody = errorData;
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // Use default error message
    }
    throw new ApiError(errorMessage, response.status, errorBody);
  }

  return response.json();
}
